import React, { useEffect, useMemo, useState } from 'react';
import { SliderModal } from '@components/molecules/SlideOver';
import {
  BudgetItemTaskGroupHeader,
  BudgetItemTaskGroupVariant,
} from '@components/molecules/BudgetItemTaskGroupHeader';
import { InvoiceLineItemRow } from '@components/molecules/InvoiceLineItemRow';
import { InvoiceTotalsSummary } from '@components/molecules/InvoiceTotalsSummary';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import {
  Invoice,
  InvoiceLineItem,
  InvoiceLineItemSourceType,
  InvoiceStatus,
  isInvoiceEditable,
} from '@models/invoice.model';

const DRAWER_TITLE_BY_STATUS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'Invoice preview',
  [InvoiceStatus.SEND_FAILED]: 'Invoice preview',
  [InvoiceStatus.SENDING]: 'Sending invoice',
  [InvoiceStatus.SENT]: 'Sent invoice',
  [InvoiceStatus.PAID]: 'Paid invoice',
  [InvoiceStatus.VOIDED]: 'Voided invoice',
};

interface LineItemGroup {
  key: string;
  title: string;
  variant: BudgetItemTaskGroupVariant;
  items: InvoiceLineItem[];
  subtotal: number;
}

const LINE_ITEMS_GROUP_KEY = '__line-items__';
const UNGROUPED_KEY = '__ungrouped__';

const buildLineItemGroups = (items: InvoiceLineItem[]): LineItemGroup[] => {
  const groups = new Map<string, LineItemGroup>();
  for (const item of items) {
    let key: string;
    let title: string;
    let variant: BudgetItemTaskGroupVariant;
    if (item.sourceType === InvoiceLineItemSourceType.LINE_ITEM) {
      key = LINE_ITEMS_GROUP_KEY;
      title = 'Project line items';
      variant = 'line-items';
    } else if (item.sourceType === InvoiceLineItemSourceType.DELEGATED_TASK_COST) {
      key = `delegated:${item.taskId || item.sourceEntityId || item.invoiceLineItemId}`;
      title = item.taskName || 'Delegated task';
      variant = 'delegated';
    } else if (item.taskId || item.taskName) {
      key = `task:${item.taskId || item.taskName}`;
      title = item.taskName || 'Task items';
      variant = 'task';
    } else {
      key = UNGROUPED_KEY;
      title = 'Other items';
      variant = 'task';
    }
    let group = groups.get(key);
    if (!group) {
      group = { key, title, variant, items: [], subtotal: 0 };
      groups.set(key, group);
    }
    group.items.push(item);
    group.subtotal += Number(item.lineTotal) || 0;
  }
  const variantOrder: Record<BudgetItemTaskGroupVariant, number> = {
    task: 0,
    delegated: 1,
    'line-items': 2,
  };
  return Array.from(groups.values()).sort((a, b) => {
    if (variantOrder[a.variant] !== variantOrder[b.variant]) {
      return variantOrder[a.variant] - variantOrder[b.variant];
    }
    return a.title.localeCompare(b.title);
  });
};

interface InvoicePreviewDrawerProps {
  isOpen: boolean;
  invoice: Invoice | null;
  organizationName?: string;
  onClose: () => void;
  onSend?: () => void;
  isSending?: boolean;
  /**
   * When provided and the invoice is editable, the description box becomes a textarea
   * that calls this handler on blur (and only when the value actually changed). The
   * returned promise lets us flush a pending edit before "Send invoice" so the backend
   * doesn't read a stale `orderDescription` from the DB during the send race.
   */
  onSaveDescription?: (description: string) => Promise<void>;
}

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

export const InvoicePreviewDrawer: React.FC<InvoicePreviewDrawerProps> = ({
  isOpen,
  invoice,
  organizationName,
  onClose,
  onSend,
  isSending = false,
  onSaveDescription,
}) => {
  const groups = useMemo(
    () => (invoice ? buildLineItemGroups(invoice.lineItems) : []),
    [invoice],
  );

  const [descriptionDraft, setDescriptionDraft] = useState(invoice?.orderDescription ?? '');

  useEffect(() => {
    setDescriptionDraft(invoice?.orderDescription ?? '');
  }, [invoice?.invoiceId, invoice?.orderDescription]);

  if (!invoice) {
    return <SliderModal isOpen={isOpen} closeSlider={onClose} title="Invoice preview" isSecondaryLayer />;
  }

  const canSend = onSend && isInvoiceEditable(invoice.status);
  const canEditDescription = Boolean(onSaveDescription) && isInvoiceEditable(invoice.status);
  const drawerTitle = DRAWER_TITLE_BY_STATUS[invoice.status] ?? 'Invoice';

  const handleDescriptionBlur = () => {
    if (!canEditDescription) return;
    const next = descriptionDraft.trim();
    if (next === (invoice.orderDescription ?? '').trim()) return;
    onSaveDescription?.(next);
  };

  const handleSendClick = async () => {
    if (!onSend) return;
    // Flush any unsaved description edit before sending. Without this the send mutation
    // races the description save and the backend reads a stale (often empty) value.
    if (canEditDescription && onSaveDescription) {
      const next = descriptionDraft.trim();
      const current = (invoice.orderDescription ?? '').trim();
      if (next !== current) {
        await onSaveDescription(next);
      }
    }
    onSend();
  };

  const actionButtons = canSend
    ? [
        {
          text: isSending ? 'Sending…' : 'Send invoice',
          variant: 'primary' as const,
          onClick: () => {
            handleSendClick();
          },
          disabled: isSending,
        },
      ]
    : undefined;

  return (
    <SliderModal
      isOpen={isOpen}
      closeSlider={onClose}
      title={drawerTitle}
      cancelText="Close"
      actionButtons={actionButtons}
      isSecondaryLayer>
      <div className="flex flex-col gap-6 pb-24">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {invoice.invoiceNumber || 'Draft invoice'}
            </h2>
            <p className="text-sm text-gray-500">
              From {organizationName || 'ZenHammer'} · To {invoice.recipientEmail}
            </p>
          </div>
          <InvoiceStatusBadge status={invoice.status} size="md" />
        </div>

        {canEditDescription ? (
          <div className="flex flex-col gap-1">
            <label
              htmlFor="invoice-preview-description"
              className="text-xs uppercase tracking-wide text-gray-500">
              Description
            </label>
            <textarea
              id="invoice-preview-description"
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              onBlur={handleDescriptionBlur}
              rows={2}
              placeholder="Add a description that will appear on the invoice (e.g., billing period, project phase, scope summary)."
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        ) : (
          invoice.orderDescription && (
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wide text-gray-500">Description</span>
              <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded whitespace-pre-wrap">
                {invoice.orderDescription}
              </p>
            </div>
          )
        )}

        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs uppercase tracking-wide text-gray-500">
                  Description
                </th>
                <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-24">
                  Qty
                </th>
                <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-28">
                  Unit
                </th>
                <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 w-28">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-400">
                    No line items.
                  </td>
                </tr>
              )}
              {groups.map((group) => (
                <React.Fragment key={group.key}>
                  <tr>
                    <td colSpan={4} className="p-0">
                      <BudgetItemTaskGroupHeader
                        title={group.title}
                        itemCount={group.items.length}
                        subtotal={group.subtotal}
                        variant={group.variant}
                      />
                    </td>
                  </tr>
                  {group.items.map((item) => (
                    <InvoiceLineItemRow
                      key={item.invoiceLineItemId}
                      item={item}
                      editable={false}
                      currency={invoice.currency}
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <InvoiceTotalsSummary
          subtotal={Number(invoice.subtotal)}
          taxRate={Number(invoice.taxRate || 0)}
          tax={Number(invoice.tax)}
          discountRate={Number(invoice.discountRate || 0)}
          discountFixedAmount={Number(invoice.discountFixedAmount || 0)}
          discountAmount={Number(invoice.discountAmount || 0)}
          total={Number(invoice.total)}
          currency={invoice.currency}
        />

        {canSend && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            <strong>Heads up.</strong> Clicking "Send invoice" registers this invoice with NMI and emails{' '}
            <span className="font-mono">{invoice.recipientEmail}</span> a branded payment link. This action
            is not reversible without voiding.
          </div>
        )}

        {invoice.lastError && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            <strong>Last error.</strong> {invoice.lastError}
          </div>
        )}

        {invoice.status === 'PAID' && invoice.paidAt && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
            Paid on {new Date(invoice.paidAt).toLocaleString()} ·{' '}
            {formatCurrency(Number(invoice.total), invoice.currency)}
          </div>
        )}
      </div>
    </SliderModal>
  );
};
